import React, { useState, useEffect } from 'react';
import { formatCurrency, getTypeColors, getTypeLabels } from '../../../shared/utils';
import { getCurrentSessionToken } from '../../auth';
import { fetchOperations } from '../../dashboard/services/operationService';
import { Pagination } from '../../../shared/components/Pagination';

export const HistoryPage: React.FC = () => {
  const [operations, setOperations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all_processed' | 'aprobada' | 'rechazada'>('all_processed');
  const [typeFilter, setTypeFilter] = useState<'all' | 'echeque' | 'transferencia'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return { label: 'Aprobada', bg: 'rgba(91,186,71,0.18)', color: '#006837' };
      case 'rejected':
        return { label: 'Rechazada', bg: 'rgba(244,67,54,0.15)', color: '#F44336' };
      case 'pending':
        return { label: 'Pendiente', bg: 'rgba(245,158,11,0.15)', color: '#B45309' };
      default:
        return { label: status, bg: 'rgba(0,0,0,0.1)', color: '#000' };
    }
  };

  const loadHistoryData = async () => {
    try {
      setIsLoading(true);
      const token = await getCurrentSessionToken();
      if (!token) return;

      const params: any = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        order: 'desc'
      };

      if (debouncedSearchTerm.trim() !== '') {
        params.search = debouncedSearchTerm.trim();
      }

      if (statusFilter !== 'all_processed') {
        params.status = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      const data = await fetchOperations(token, params);
      
      const safeDate = (dateStr: string) => 
        new Date(
          dateStr.replace(' ', 'T').includes('Z') 
            ? dateStr.replace(' ', 'T') 
            : `${dateStr.replace(' ', 'T')}Z`
        ).toLocaleString('es-AR', {
          timeZone: 'America/Argentina/Buenos_Aires',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(',', '');

      // If statusFilter is 'all_processed', we filter out 'pendiente' on client side
      const filteredItems = statusFilter === 'all_processed'
        ? data.items.filter((op: any) => op.status !== 'pendiente')
        : data.items;

      const mapped = filteredItems.map((op: any) => ({
        id: op.id,
        date: op.execution_date ? safeDate(op.execution_date) : safeDate(op.request_date),
        producer: op.producer_name || 'Desconocido',
        type: op.type === 'echeque' ? 'e-cheque' : 'transferencia',
        amount: op.amount,
        operator: op.managed_by_name || 'Sistema',
        status: op.status === 'aprobada' ? 'approved' : op.status === 'rechazada' ? 'rejected' : 'pending',
        reason: op.rejection_reason || '-'
      }));

      setOperations(mapped);
      setTotalPages(data.pages || 1);
    } catch (err) {
      console.error("Failed to load history data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, typeFilter]);

  // Load history data when dependencies change
  useEffect(() => {
    loadHistoryData();
  }, [currentPage, debouncedSearchTerm, statusFilter, typeFilter]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#212121]">Historial de Operaciones</h1>
          <p className="text-muted-foreground mt-1 mb-4">
            Registro cronológico de todas las solicitudes procesadas
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input 
                type="text" 
                placeholder="Buscar por productor, referencia, descripción o monto..."
                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm text-[#212121]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:w-[450px]">
              <select 
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm font-medium text-[#212121]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all_processed">Todos los estados</option>
                <option value="aprobada">Aprobadas</option>
                <option value="rechazada">Rechazadas</option>
              </select>

              <select 
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm font-medium text-[#212121]"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
              >
                <option value="all">Todos los tipos</option>
                <option value="echeque">E-Cheque</option>
                <option value="transferencia">Transferencia</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b text-[#212121]">
                <th className="text-left p-4 font-semibold whitespace-nowrap">Fecha/Hora Registro</th>
                <th className="text-left p-4 font-semibold">Productor</th>
                <th className="text-left p-4 font-semibold">Tipo</th>
                <th className="text-right p-4 font-semibold">Monto</th>
                <th className="text-left p-4 font-semibold">Operador</th>
                <th className="text-center p-4 font-semibold">Resultado</th>
                <th className="text-left p-4 font-semibold">Motivo Rechazo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="animate-spin h-8 w-8 text-primary mb-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Cargando historial de operaciones...</span>
                    </div>
                  </td>
                </tr>
              ) : (
                operations.map(op => {
                  const statusConfig = getStatusConfig(op.status);
                  
                  return (
                    <tr key={op.id} className="border-b hover:bg-muted/30 text-[#212121]">
                      <td className="p-4 whitespace-nowrap text-sm text-gray-600">{op.date}</td>
                      <td className="p-4 font-medium">{op.producer}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs border border-border rounded-md ${getTypeColors(op.type)}`}>
                          {getTypeLabels(op.type)}
                        </span>
                      </td>
                      <td className="p-4 text-right font-semibold">{formatCurrency(op.amount)}</td>
                      <td className="p-4 text-sm text-gray-600">{op.operator}</td>
                      <td className="p-4 text-center">
                        <span 
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-bold" 
                          style={{ background: statusConfig.bg, color: statusConfig.color }}
                        >
                          <span 
                            className="w-1.5 h-1.5 rounded-full" 
                            style={{ background: statusConfig.color }}
                          ></span>
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-500 max-w-[200px] truncate" title={op.reason !== '-' ? op.reason : ''}>
                        {op.reason}
                      </td>
                    </tr>
                  );
                })
              )}
              {!isLoading && operations.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No hay registros en el historial
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={setCurrentPage} 
        />
      </div>
    </div>
  );
};
